define((require) => {
    "use strict";

    const {DateUtil}    = require("shared/util/DateUtil");
    const {Logger}      = require("shared/util/Logger");
    const Syncer      = require("shared/services/sync/Syncer");
    const SyncManager = require("shared/services/sync/SyncManager");

    const log = new Logger("Time");

    let serverTime = 0;
    let clientTime = 0; // user time last time "serverTime" was updated, for "ticks"...

    let failed = false;

    const target = { // for queueing
        id: "time",
        toString() {
            const server = serverTime ? Time.server(true) : 0; // needed to describe the different scenarios
            const client = Time.client();
            let s = "Time[";

            if (server) {
                s += `server: ${DateUtil.formatYYYYMMDDHHmm(server)}; `;
            }

            s += `client: ${DateUtil.formatYYYYMMDDHHmm(client)}`;

            if (server) {
                s += "; server-";

                if (server > client) {
                    s += `ahead: ${DateUtil.timeDifference(server, client)}`;
                } else {
                    s += `behind: ${DateUtil.timeDifference(client, server)}`;
                }

                s += ` (${(server - client)}ms)`;
            }

            return `${s}]`;
        }
    };

    Syncer.State.init(target, {
        immutable: true,
        unique:    ["id"]
    });

    /**
     * @class {Time}
     *
     * The <tt>Time</tt> type is used to provide correct dates and times for the <i>server</i> and <i>client</i>
     * times. For model properties normally timestamped by the backend, it is not safe to use the client time
     * because of time zone differences. For each such model property, for example "editedAt", the server time
     * must be used to ensure correctness. For pure frontend behaviour, it suffices to use the client time.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     */
    const Time = {

        /**
         * Returns a promise that will resolve with the server time in ms since the epoch.
         *
         * @return {Promise} A promise that will resolve with the server time in ms since the epoch.
         */
        update() {
            return SyncManager.get("time", target).done((timeFromServer) => {
                serverTime = parseInt(timeFromServer, 10) || 0; // NaN -> 0
                clientTime = serverTime ? Time.client() : 0;
                log.info(`Server time updated: ${serverTime}ms -> ${target}`);
            }).fail(() => {
                failed = true;
            });
        },

        /**
         * Returns the (estimated) server time in ms seconds since the epoch in a synchronous fashion. That is,
         * the current estimated server time ("ticked") is returned immediately, calculated based on the last
         * known server time. <p>
         *
         * Always prefer this time over client time when setting timestamps, as user time cannot be trusted. This
         * method may also trigger a silent update of the server time in the background if the last actual server
         * time retrieved is older than 10 minutes, but only if <tt>noUpdate</tt> is falsey.
         *
         * @param  {Boolean} [noUpdate] True to skip a background update of the server time always, false to update if
         *                              its time to do so (default).
         * @return {Number} The server time in ms since the epoch.
         * @throws {Error}  If a previous call of the update method has failed to get the time, an error will be thrown.
         */
        server(noUpdate) {
            if (failed) {
                throw new Error("Failed fetching server time.");
            }

            // User time...
            const client = this.client();
            const ticks  = clientTime ? Math.max(client - clientTime, 0) : 0;

            // Update in the background if it has been more than 10 minutes since last update...
            if (!noUpdate && (!serverTime || ticks > 600000)) { // 1000 ms/s * 60 s/m * 10 m
                this.update();
            }

            // We may not have a server time yet... :/ But, if we do, add ms passed since last server time was fetched
            // to make the time tick. This cannot be guaranteed to be absolute precise, but as close as we can get
            // when requesting the time synchronously like this...
            if (serverTime) {
                return serverTime + ticks;
            }

            log.verbose(`No server time available yet, using client time: ${this}`);
            return clientTime;
        },

        /**
         * Returns the client (user) time in ms seconds since the epoch. <p>
         *
         * The client time <b>cannot</b> be trusted as the client machine can be set to any date/time.
         *
         * @return {Number} The client time in ms since the epoch.
         */
        client() {
            return Date.now();
        }

    };

    return Time;
});



// WEBPACK FOOTER //
// ./src/shared/services/Time.js